/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { toTitleCase, date } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Encounter, RoundHistory } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';

interface EncountersHistoryTableProps {
  encounterData?: Encounter | null;
  roundHistory: RoundHistory[] | null;
  gotoRoundPage: (round: RoundHistory) => void;
}
const EncountersHistoryTable: FC<EncountersHistoryTableProps> = ({
  encounterData,
  roundHistory,
  gotoRoundPage
}) => {
  const [filteredPtr, setFilteredPtr] = useState<any[]>([]);

  const [sortKey, setSortKey] = useState<string>('roundDate');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Timestamp');

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Timestamp':
        key = 'roundDate';
        break;
      case 'ADT':
        key = 'msgtype';
        break;
      case 'Status':
        key = '_patientStatus';
        break;
      case 'Unit':
        key = 'pointofcare';
        break;
      case 'Room':
        key = 'room';
        break;
      case 'Bed':
        key = 'bed';
        break;
      case 'Census':
        key = '_census';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const parseADT = (message: string | undefined) => {
    const lastTwo =
      (message && message.substring(message.length - 2, message.length)) || '';

    switch (lastTwo) {
      case '01':
        return 'Admitted/Visited Notification';
      case '02':
        return 'Transferred a Patient';
      case '03':
        return 'Discharged/Ended Visit';
      case '04':
        return 'Registered a Patient';
      case '05':
        return '';
      case '06':
        return 'Changed an Outpatient to an Inpatient';
      case '07':
        return '';
      case '08':
        return 'Updated Patient Information';
      case '09':
        return 'Patient Departed';
      case '20':
        return 'Updated Bed Status';
      default:
        return '';
    }
  };

  const checkInCensus = (history: Encounter) => {
    let admissionCheck: boolean;
    const today = new Date();
    const formattedAdmitTime = new Date(
      Number(encounterData?.admittime?.slice(0, 4)),
      parseInt(String(encounterData?.admittime?.slice(4, 6)), 10) - 1,
      Number(encounterData?.admittime?.slice(6, 8)),
      Number(encounterData?.admittime?.slice(8, 10)),
      Number(encounterData?.admittime?.slice(10, 12))
    );

    const formattedDischargeTime = new Date(
      Number(history.dischargetime?.slice(0, 4)),
      parseInt(history.dischargetime?.slice(4, 6), 10) - 1,
      Number(history.dischargetime?.slice(6, 8)),
      Number(history.dischargetime?.slice(8, 10)),
      Number(history.dischargetime?.slice(10, 12))
    );

    const admitTimeNotInFuture = formattedAdmitTime <= today;
    const dischargedTimeNotInFuture = formattedDischargeTime <= today;
    const dischargeNotNull = history.dischargetime && history.dischargetime;

    if (
      history.admissionlevel === 'DIS' ||
      history.admissionlevel === 'ADM' ||
      history.admissionlevel === 'REG'
    )
      admissionCheck = true;
    else admissionCheck = false;

    const hasUnit = history && history.pointofcare;

    if (
      hasUnit &&
      admissionCheck &&
      admitTimeNotInFuture &&
      dischargedTimeNotInFuture &&
      dischargeNotNull
    )
      return 'Y';
    return 'N';
  };

  const sortedPtr = useMemo(() => {
    const stateCopy = [...filteredPtr];
    const sorted = stateCopy.sort((a: RoundHistory, b: RoundHistory) => {
      if (a[sortKey] === b[sortKey]) {
        return 0;
      }
      if (!a[sortKey]) {
        return sortDir === 'asc' ? 1 : -1;
      }
      if (!b[sortKey]) {
        return sortDir === 'asc' ? -1 : 1;
      }
      if (sortDir === 'asc') {
        return a[sortKey]! > b[sortKey]! ? 1 : -1;
      }
      return a[sortKey]! > b[sortKey]! ? -1 : 1;
    });
    return sorted;
  }, [filteredPtr, sortDir, sortKey]);

  const isTableEmpty = useMemo(() => sortedPtr.length === 0, [sortedPtr]);

  useEffect(() => {
    if (encounterData?.history.length || roundHistory?.length) {
      // deep copying history data, this doesnt exist often, typing any
      const cleanEncounterData =
        encounterData?.history?.map((history: any) => {
          let reverseFormattedTime;
          const reverseFormattedDate = `${
            history.msgtime?.split(' ')[0].split('/')[2]
          }${history.msgtime?.split(' ')[0].split('/')[0]}${history.msgtime
            ?.split(' ')[0]
            .split('/')[1]
            .padStart(2, '0')}`;

          if (history.msgtime?.split(' ')[2] === 'AM') {
            reverseFormattedTime = `${
              history.msgtime?.split(' ')[1].split(':')[0]
            }${history.msgtime?.split(' ')[1].split(':')[1]}${
              history.msgtime?.split(' ')[1].split(':')[2]
            }`;
          }
          if (history.msgtime?.split(' ')[2] === 'PM') {
            reverseFormattedTime = `${
              parseInt(history.msgtime?.split(' ')[1].split(':')[0], 10) + 12
            }${history.msgtime?.split(' ')[1].split(':')[1]}${
              history.msgtime?.split(' ')[1].split(':')[2]
            }`;
          }

          const reverseFormattedDatetime = `${reverseFormattedDate}${reverseFormattedTime}`;

          return {
            ...history,
            _patientStatus: encounterData?.patientstatus,
            _adtMessage: parseADT(encounterData?.msgtype),
            _date: history.msgtime.split(' ')[0],
            _time: history.msgtime.split(' ')[1],
            _census: checkInCensus(history),
            roundDate: reverseFormattedDatetime // for sorting purpose
          };
        }) ?? [];

      // deep copying history data, this doesnt exist often, typing any
      const cleanRoundHistoryData =
        roundHistory?.map((history: RoundHistory) => {
          let roundDateCorrected = history.roundDate;

          if (roundDateCorrected.length < 14) {
            roundDateCorrected = roundDateCorrected.padEnd(14, '0');
          }

          const dateTime = date
            .dateTimeCombination(roundDateCorrected)
            .replace(', ', ' ');
          const rounder = `${history.userFirstName} ${history.userLastName} Rounded`;
          const roundedOn = `on ${toTitleCase(
            encounterData?.firstname
          )} ${toTitleCase(encounterData?.lastname)}`;

          return {
            ...history,
            _date: dateTime.split(' ')[0],
            _time: dateTime.split(' ').slice(1),
            msgtype: rounder,
            _adtMessage: roundedOn,
            pointofcare: history.unitId,
            _patientStatus: '-'
          };
        }) ?? [];

      setFilteredPtr([...cleanEncounterData, ...cleanRoundHistoryData]);
    }
  }, [encounterData, roundHistory]);

  const headers: JSX.Element[] = [
    // <NeuTable style={{ height: 'calc(100vh - 280px)' }}>
    <CellContainer
      key="Timestamp"
      id="Encounters-History-Table-Timestamp-Column"
      unit="column"
      colWidth={21}
      direction={!(active === 'Timestamp') ? 'none' : sortDir}
      sort
    >
      Timestamp
    </CellContainer>,
    <CellContainer
      key="ADT"
      id="Encounters-History-Table-ADT-Column"
      unit="column"
      colWidth={23}
      direction="none"
      // direction={!(active === 'ADT') || sortDir === 'none' ? 'asc' : sortDir}
    >
      ADT
    </CellContainer>,
    <CellContainer
      key="Status"
      id="Encounters-History-Table-ADT-Column"
      unit="column"
      colWidth={14}
      direction={!(active === 'Status') ? 'none' : sortDir}
      sort
    >
      Status
    </CellContainer>,
    <CellContainer
      key="Unit"
      id="Encounters-History-Table-Unit-Column"
      unit="column"
      colWidth={12}
      direction={!(active === 'Unit') ? 'none' : sortDir}
      sort
    >
      Unit
    </CellContainer>,
    <CellContainer
      key="Room"
      id="Encounters-History-Table-Room-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Room') ? 'none' : sortDir}
      sort
    >
      Room
    </CellContainer>,
    <CellContainer
      key="Bed"
      id="Encounters-History-Table-Bed-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Bed') ? 'none' : sortDir}
      sort
    >
      Bed
    </CellContainer>,
    <CellContainer
      key="Census"
      id="Encounters-History-Table-Census-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Census') ? 'none' : sortDir}
      sort
    >
      Census
    </CellContainer>
  ];

  const rows = sortedPtr?.map((history: RoundHistory, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Encounter-Row-${i}`}
        id={`Encounter-Row-${history.msgid || history.roundId}`}
        unit="row"
        height="5.5em"
        size="default"
      >
        <CellContainer unit="column" colWidth={21}>
          <StyledLabel>
            <div className="d-flex flex-column">
              <div>{`${history._date}`}</div>
              <div>{`${(history._time ?? [])
                .toString()
                .replace(',', ' ')}`}</div>
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={23}>
          <StyledLabel
            role="button"
            onClick={history.roundId ? () => gotoRoundPage(history) : undefined}
          >
            {history.roundId ? (
              <div
                className="d-flex flex-column"
                style={{
                  color: '#206DA3',
                  fontWeight: 700
                }}
              >
                <div>{`${history.msgtype}`}</div>
                <div>{`${history._adtMessage}`}</div>
              </div>
            ) : (
              <div className="d-flex flex-column">
                <div>{`${history.msgtype?.replace('ADT^', '') ?? ''}`}</div>
                <div>{`${history._adtMessage}`}</div>
              </div>
            )}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={14}>
          <StyledLabel>
            {history._patientStatus ? history._patientStatus : '-'}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={12}>
          <StyledLabel>{history.pointofcare}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>{history.room ? history.room : 'Unknown'}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>{history.bed ? history.bed : 'Unknown'}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>{history._census ? history._census : '-'}</StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="encounter-history-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      type="encounters"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default EncountersHistoryTable;
